import { useSyncExternalStore, useCallback } from 'react';

/**
 * Teen mode (14+) — a presentation-layer safety filter for the China pilot.
 *
 * When enabled, therapy entries whose `teen_status` is `excluded` are hidden
 * everywhere (wiki, booking search, AI grounding), and `restricted` entries are
 * shown with a guardian notice. This never changes the underlying `min_age`
 * semantics of an entry and is off by default for the international site.
 */

const STORAGE_KEY = 'resonow_teen_mode';
const EVENT = 'resonow:teen-mode-change';

export type TeenStatus = 'approved' | 'restricted' | 'excluded';

const read = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

let cached = read();

const subscribe = (onChange: () => void) => {
  const handler = () => {
    cached = read();
    onChange();
  };
  window.addEventListener(EVENT, handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener(EVENT, handler);
    window.removeEventListener('storage', handler);
  };
};

const getSnapshot = () => cached;

export const setTeenMode = (enabled: boolean) => {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false');
  } catch {
    /* storage unavailable — keep the in-memory value */
  }
  cached = enabled;
  window.dispatchEvent(new Event(EVENT));
};

export const isTeenModeEnabled = () => cached;

export const useTeenMode = () => {
  const enabled = useSyncExternalStore(subscribe, getSnapshot, () => false);
  const setEnabled = useCallback((value: boolean) => setTeenMode(value), []);
  return { teenMode: enabled, setTeenMode: setEnabled };
};

/** Hide `excluded` entries when teen mode is on. Keeps `approved` + `restricted`. */
export const filterForTeenMode = <T extends { teen_status?: string | null }>(
  rows: T[] | undefined,
  teenMode: boolean
): T[] => {
  if (!rows) return [];
  if (!teenMode) return rows;
  return rows.filter((r) => r.teen_status !== 'excluded');
};
